import React, { useState, useEffect, useContext, Fragment } from "react";

import MathContent from "util/mathjax";
import {
  Box,
  Columns,
  Form,
  Heading,
  Element,
  Message,
} from "react-bulma-components";
import dayjs from "dayjs";
import minMax from "dayjs/plugin/minMax";

import ErrorMessage from "util/errorMessage";
import StructuredText from "common-frontend/util/structuredText";

import { Dollars } from "util/currency";
import { FORM_STATE } from "./registrationForms";
import { PromptQuestions, adjustQuestionResponses } from "./questions";

import deepEquals from "react-fast-compare";
import IconButton from "util/iconButton";
import { FaPlus, FaTimes } from "react-icons/fa";

import RefundContactNotice from "./refundContactNotice";
import capacityInformation from "./capacity";

import { participationStateContext } from "./load";
import {
  adjustFeeQualifyingDetails,
  AskFeeQualifyingData,
} from "./feeQualification";

import { ucFirst } from "util/text";

const { Field, Label, Control, Radio, Select, Checkbox } = Form;
dayjs.extend(minMax);

export const IN_PERSON = "in person";
export const REMOTELY = "remotely";

export const isFalse = (v) => !(v ?? true);
export const isRemotelyOrInPerson = (v) => v === IN_PERSON || v === REMOTELY;
export const isSimpleTrue = (v) => v && !isRemotelyOrInPerson(v);

const ShowStatus = ({ origSel, nowSel, children }) => {
  const classes = ["talk-status"];
  if (origSel) classes.push("is-underlined");
  if (nowSel) classes.push("has-text-weight-bold");
  if (nowSel && !origSel) classes.push("has-text-success-dark");
  return <span className={classes.join(" ")}>{children}</span>;
};

// The PromptAttendance component doesn't directly handle capacity limits
// for in-person attendance, but supports "inPersonWording" and "remoteWording"
// props that will be displayed in place of the regular "in person" / "remotely"
// in order to say something like "remotely, but add me to the waiting list
// for in-person". The caller is responsible for providing suitable
// "attending", "setAttending" props that translate between IN_PERSON/REMOTE
// (seen by the <PromoptAttendance> component) and the actual
// "attending=REMOTE, waitlist_desired=X" values used in the registration.

export function PromptAttendance({
  attending,
  origAttending,
  setAttending,
  hasRemoteOption,
  inPersonWording = "in person",
  remoteWording = "remotely",
  promptHowOnly,
  radioName,
}) {
  if (promptHowOnly && !hasRemoteOption) return null;
  const wasSimpleTrue = isSimpleTrue(origAttending) && !promptHowOnly;
  const yesPrefix = promptHowOnly ? "" : "Yes - ";
  return (
    <Fragment>
      <Control>
        {hasRemoteOption && !wasSimpleTrue ? (
          <Fragment>
            <Radio
              name={radioName}
              value={IN_PERSON}
              checked={attending === IN_PERSON}
              onChange={() => setAttending(IN_PERSON)}
            >
              <ShowStatus
                origSel={origAttending === IN_PERSON}
                nowSel={attending === IN_PERSON}
              >
                {ucFirst(yesPrefix + inPersonWording)}
              </ShowStatus>
            </Radio>
            <Radio
              name={radioName}
              value={REMOTELY}
              checked={attending === REMOTELY}
              onChange={() => setAttending(REMOTELY)}
            >
              <ShowStatus
                origSel={origAttending === REMOTELY}
                nowSel={attending === REMOTELY}
              >
                {ucFirst(yesPrefix + remoteWording)}
              </ShowStatus>
            </Radio>
          </Fragment>
        ) : (
          <Fragment>
            <Radio
              name={radioName}
              value={true}
              checked={!!attending}
              onChange={() => {
                // Don't update to a "simple" true if a remote/in-person
                // option has already been selected.
                if (!attending) setAttending(true);
              }}
            >
              <ShowStatus origSel={!!origAttending} nowSel={!!attending}>
                Yes
              </ShowStatus>
            </Radio>
            {hasRemoteOption ? (
              <div
                className={
                  "inline-box " +
                  (isSimpleTrue(attending)
                    ? "enabled missing"
                    : attending
                    ? "enabled"
                    : "disabled")
                }
              >
                <Control>
                  <Radio
                    name={radioName + "-how"}
                    value={IN_PERSON}
                    checked={attending === IN_PERSON}
                    onChange={() => setAttending(IN_PERSON)}
                    disabled={!attending}
                  >
                    <ShowStatus
                      origSel={false}
                      nowSel={attending === IN_PERSON}
                    >
                      In Person
                    </ShowStatus>
                  </Radio>
                  <Radio
                    name={radioName + "-how"}
                    value={REMOTELY}
                    checked={attending === REMOTELY}
                    onChange={() => setAttending(REMOTELY)}
                    disabled={!attending}
                  >
                    <ShowStatus origSel={false} nowSel={attending === REMOTELY}>
                      Remotely
                    </ShowStatus>
                  </Radio>
                </Control>
              </div>
            ) : null}{" "}
          </Fragment>
        )}
        {promptHowOnly ? null : (
          <Radio
            name={radioName}
            value={false}
            checked={isFalse(attending)}
            onChange={() => setAttending(false)}
          >
            <ShowStatus
              origSel={isFalse(origAttending)}
              nowSel={isFalse(attending)}
            >
              No
            </ShowStatus>
          </Radio>
        )}
      </Control>
    </Fragment>
  );
}

export function PromptTalkAttendance({ talk, update }) {
  const {
    id,
    attending,
    origAttending,
    hasRemoteOption: talkHasRemoteOption,
  } = talk;

  const hasRemoteOption =
    talkHasRemoteOption ||
    isRemotelyOrInPerson(attending) ||
    isRemotelyOrInPerson(origAttending);

  return (
    <Fragment>
      <Columns.Column size="one-third">
        <PromptAttendance
          {...{ attending, origAttending, hasRemoteOption }}
          setAttending={(v) => update("attending_talk_" + id, v)}
          radioName={"attending-" + id}
        />
      </Columns.Column>

      <Columns.Column size="two-thirds">
        <div className="talk-reg-date-speaker">
          {dayjs(talk.date).format("MMMM D") +
            " at " +
            talk.time +
            ": " +
            talk.speaker}
        </div>
        <div className="talk-reg-title">
          <MathContent inline>{talk.title}</MathContent>
        </div>
      </Columns.Column>
    </Fragment>
  );
}

function PromptArrivalOrDeparture({
  isArrival,
  label,
  currentDayJS,
  startDayJS,
  endDayJS,
  setDayJS,
  deleteButton,
}) {
  const selectedDate =
    currentDayJS && currentDayJS >= startDayJS && currentDayJS <= endDayJS
      ? currentDayJS
      : null;

  const [selectedMonth, setSelectedMonth] = useState(
    selectedDate ? selectedDate.startOf("Month") : null
  );
  useEffect(() => {
    if (
      selectedMonth < startDayJS.startOf("month") ||
      selectedMonth > endDayJS.startOf("month")
    )
      setSelectedMonth(null);
  }, [selectedMonth, startDayJS, endDayJS]);

  useEffect(() => {
    if (
      currentDayJS &&
      (currentDayJS < startDayJS || currentDayJS > endDayJS)
    ) {
      setDayJS(null);
    }
  });

  const getMonthValue = (month) => (month ? month.format("YYYY-MM") : "");

  const monthOptions = selectedMonth ? [] : [null];
  let monthOption = startDayJS.startOf("month");
  while (monthOption < endDayJS) {
    monthOptions.push(monthOption);
    monthOption = monthOption.add(1, "month");
  }
  const monthOptionLookup = Object.fromEntries(
    monthOptions.map((opt) => [getMonthValue(opt), opt])
  );

  const dayOptions = selectedDate ? [] : [null];
  const getDayValue = (day) => (day ? day.format("YYYY-MM-DD") : "");

  if (selectedMonth) {
    let dayOption = selectedMonth < startDayJS ? startDayJS : selectedMonth;

    while (dayOption <= selectedMonth.endOf("month") && dayOption <= endDayJS) {
      dayOptions.push(dayOption);
      dayOption = dayOption.add(1, "day");
    }
  }
  const dayOptionLookup = Object.fromEntries(
    dayOptions.map((opt) => [getDayValue(opt), opt])
  );

  const onChangeMonth = (newMonthValue) => {
    const newMonth = monthOptionLookup[newMonthValue];
    if (!newMonth) return;
    if (newMonth < monthOptions[0]) return;
    if (newMonth > monthOptions.slice(-1)[0]) return;
    if (selectedMonth === newMonth) return;
    setSelectedMonth(newMonth);
    setDayJS(undefined);
  };

  const onChangeDay = (newDayValue) => {
    const newDay = dayOptionLookup[newDayValue];
    if (!newDay) return;
    if (newDay < startDayJS) return;
    if (newDay > endDayJS) return;
    setDayJS(newDay);
    setSelectedMonth(newDay.startOf("month"));
  };

  return (
    <Field>
      <Label>{label}</Label>
      <Field kind="group">
        <Control>
          <Select
            value={getMonthValue(selectedMonth)}
            onChange={(e) => onChangeMonth(e.target.value)}
          >
            {monthOptions.map((opt) => (
              <option key={getMonthValue(opt)} value={getMonthValue(opt)}>
                {opt ? opt.format("MMMM") : " - please select month - "}
              </option>
            ))}
          </Select>
        </Control>
        {selectedMonth ? (
          <Control>
            <Select
              value={getDayValue(selectedDate)}
              onChange={(e) => onChangeDay(e.target.value)}
            >
              {dayOptions.map((opt) => (
                <option key={getDayValue(opt)} value={getDayValue(opt)}>
                  {opt ? opt.format("D") : " - please select day - "}
                </option>
              ))}
            </Select>
          </Control>
        ) : null}
        {deleteButton ? (
          <Control>
            <IconButton icon={FaTimes} onClick={deleteButton.action}>
              {deleteButton.wording}
            </IconButton>
          </Control>
        ) : null}
      </Field>
    </Field>
  );
}

function PromptAttendanceDates({
  isProgram,
  currentDates,
  setDates,
  startDate,
  endDate,
}) {
  const dates = currentDates && currentDates.length > 0 ? currentDates : [{}];

  const moreThanOneVisit = isProgram
    ? "More than one visit period?"
    : "Attending more than once?";
  const moreThanSeveralVisits = (number) =>
    isProgram
      ? `More than ${number} visits?`
      : `Attending more than ${number} times?`;

  const additionalVisitHeader =
    dates.length === 1 ? moreThanOneVisit : moreThanSeveralVisits(dates.length);

  const singleVisitName = isProgram ? "your visit" : "attendance";
  const singleVisitNameIfMore = isProgram
    ? "your first visit"
    : "your first attendance period";
  const oneOfSeveralVisitsName = (which) =>
    isProgram ? "visit #" + which : "attendance period #" + which;
  const visitName = (which) =>
    dates.length === 1 ? singleVisitName : oneOfSeveralVisitsName(which);
  const visitNameIfMore = (which) =>
    dates.length === 1 ? singleVisitNameIfMore : oneOfSeveralVisitsName(which);
  const additionalVisitName = isProgram ? "visit" : "attendance period";

  const deleteVisit = (i) => {
    if (dates.length === 0) return;
    setDates([...dates.slice(0, i), ...dates.slice(i + 1)]);
  };

  const nextAvailableDateAfterVisit = [];
  dates.forEach((d, i) => {
    const availableAfterPreviousVisit =
      i === 0 ? dayjs(startDate) : nextAvailableDateAfterVisit[i - 1];
    const usedDates = [d.arrival, d.departure]
      .filter((d) => !!d)
      .map((d) => dayjs(d));
    nextAvailableDateAfterVisit[i] = dayjs.max([
      availableAfterPreviousVisit,
      ...usedDates.map((d) => d.add(2, "days")),
    ]);
  });

  const endDayJS = dayjs(endDate);
  const extraneous = dates
    .map((d, i) => i)
    .filter((i) => i > 0 && nextAvailableDateAfterVisit[i - 1] > endDayJS);

  useEffect(() => extraneous.forEach(deleteVisit));
  return (
    <Fragment>
      {dates.map(({ arrival, departure }, i) => {
        const what = visitName(i + 1);

        const setDate = (newDayJS, position, field) =>
          setDates(
            dates.map((d, i) =>
              i === position
                ? {
                    ...d,
                    [field]: newDayJS?.format("YYYY-MM-DD"),
                  }
                : { ...d }
            )
          );
        const startDayJS =
          i > 0 ? nextAvailableDateAfterVisit[i - 1] : dayjs(startDate);
        return (
          <Fragment key={i}>
            <PromptArrivalOrDeparture
              label={"First day of " + what}
              isArrival
              currentDayJS={arrival ? dayjs(arrival) : null}
              startDayJS={startDayJS}
              endDayJS={dayjs(endDate)}
              setDayJS={(newDayJS) => setDate(newDayJS, i, "arrival")}
              deleteButton={
                dates.length > 1
                  ? {
                      action: () => deleteVisit(i),
                      wording: "Delete this " + additionalVisitName,
                    }
                  : null
              }
            />

            {arrival ? (
              <PromptArrivalOrDeparture
                key={"departure" + i}
                label={"Last day of " + what}
                isArrival={false}
                currentDayJS={departure ? dayjs(departure) : null}
                startDayJS={arrival ? dayjs(arrival) : startDayJS}
                endDayJS={dayjs(endDate)}
                setDayJS={(newDayJS) => setDate(newDayJS, i, "departure")}
              />
            ) : null}
            {i < dates.length - 1 ? null : !arrival ? null : (
              <Field>
                <Label>{additionalVisitHeader}</Label>
                {!departure ? (
                  <Fragment>
                    After entering the last day of {visitNameIfMore(i + 1)}, you
                    will be able to add dates for an additional{" "}
                    {additionalVisitName} if coming more than{" "}
                    {dates.length > 1 ? dates.length + " times" : "once"}.
                  </Fragment>
                ) : nextAvailableDateAfterVisit[i] > endDayJS ? (
                  <Fragment>
                    If you enter an earlier last day for{" "}
                    {visitNameIfMore(i + 1)}, you will be able to add dates for
                    an additional {additionalVisitName}.
                  </Fragment>
                ) : (
                  <IconButton
                    icon={FaPlus}
                    onClick={() => setDates([...dates, {}])}
                  >
                    Add another {additionalVisitName}
                  </IconButton>
                )}
              </Field>
            )}
          </Fragment>
        );
      })}
    </Fragment>
  );
}

function PromptWaitingList({
  isActivityAttendance,
  isProgram,
  waitingList,
  currentDetails,
  adjustDetails,
}) {
  const onWaitingList =
    waitingList.position !== null && !waitingList.positionIsForInPerson;

  return (
    <Message color="danger">
      <Message.Body>
        This
        {isActivityAttendance
          ? isProgram
            ? " program "
            : " event "
          : " item "}
        is at capacity
        {onWaitingList ? (
          <Fragment>
            , but your name is on a waiting list in case of cancellations. You
            are currently at position #{waitingList.position + 1} on the waiting
            list.
          </Fragment>
        ) : (
          <Fragment>
            , so it is not possible to register now, but, if you wish, you may
            place your name on a waiting list in case of cancellations. There{" "}
            {waitingList.length === 0
              ? "are no people "
              : waitingList.length === 1
              ? "is one person "
              : `are ${waitingList.length} people `}
            currently on the waiting list.
          </Fragment>
        )}
      </Message.Body>
      <Field>
        <Control>
          <Checkbox
            checked={!!currentDetails.waitlist_desired}
            onChange={(e) =>
              adjustDetails({
                waitlist_desired: e.target.checked ? true : null,
              })
            }
          >
            {onWaitingList
              ? " Keep me on the waiting list"
              : " Add me to the waiting list"}
          </Checkbox>
        </Control>
      </Field>
    </Message>
  );
}

export function RegistrationAttendanceItem({
  itemKey,
  item,
  participation,
  currentDetails,
  openFor,
  previouslyOpenFor,
  attendingActivity,
  wasAttendingActivity,
  canAssumeAttendingPrimaryActivity,
  showMainAttendanceHeading,
  setDetails,
}) {
  const {
    activity_id,
    item_code,
    attending_wording,
    fee_categories,
    remote_attendance_option,
    confirmation_display,
    already_paid = 0,
    unavailable,
    prompt_attendance_dates,
    questions,
    is_past,
    last_day_available,
    deadline_passed_message,
  } = item;

  // Current registration state for this item:
  const currentRegistration =
    participation?.current_registration_item_data?.[activity_id]?.attendance?.[
      item_code
    ];

  // Current registration state for attendance at the overall activity of which
  // this item is a part:
  const currentActivityAttendanceData =
    participation?.current_registration_item_data?.[activity_id]?.attendance?.[
      ""
    ];
  const hadActivityAttendanceRecorded =
    currentActivityAttendanceData?.item_values?.attending !== undefined &&
    currentActivityAttendanceData?.item_values?.attending !== null;

  // Is this item the "attendance" item for the overall activity of which
  // this item is a part?
  const isActivityAttendance = item_code === "";

  // Is this item the "attendance" item for the overall activity that this
  // registration is primarily for, and not merely for the overall activity
  // in a related-activity-registration item?
  const isPrimaryActivityAttendance =
    activity_id === participation.activity.id && isActivityAttendance;

  // Is this item the "attendance" item for some part (but not the overall
  // activity) of the activity that this registration is primarily for?
  const isPrimarySubactivityAttendance =
    activity_id === participation.activity.id && !isActivityAttendance;

  const isProgram = participation.activity.is_program;
  const isProgramVisitAttendance =
    isProgram &&
    activity_id === participation.activity.id &&
    item_code === "program_itself";

  // FIXME: Show other inapplicable fee categories too, for information about
  // what the fees are for different types of people, so the registrant can
  // make sure they are getting the right one.
  const applicableCategories = fee_categories?.filter((c) => c.applicable);
  const feeInfo =
    applicableCategories && applicableCategories.length > 0
      ? applicableCategories[0]
      : {};
  const {
    amount: in_person_amount,
    remote_attendance_amount,
    deadline,
    deposit_amount,
    deposit_deadline,
  } = feeInfo;

  const cancellationPolicy = feeInfo.cancellation_policy;

  // Select amount based on in-person attendance, remote attendance, or not
  // attending at all. Do this in a callback so we can easily recalculate when
  // changing attendance type or fee_selection_data.
  const getApplicableFee = (attending) => {
    const amount = !attending
      ? 0
      : attending === REMOTELY
      ? remote_attendance_amount
      : in_person_amount;
    const depositApplicable =
      deposit_amount && deposit_amount > 0 && deposit_amount < amount;

    const depositAmountDue =
      depositApplicable && already_paid < deposit_amount
        ? deposit_amount - already_paid
        : 0;

    const totalAmountDue = already_paid < amount ? amount - already_paid : 0;
    return { amount, depositApplicable, depositAmountDue, totalAmountDue };
  };

  const { amount, depositApplicable, totalAmountDue } = getApplicableFee(
    currentDetails?.attending
  );

  const depositPaid = depositApplicable && already_paid >= deposit_amount;

  const depositPartiallyPaid =
    depositApplicable && already_paid > 0 && !depositPaid ? already_paid : 0;

  const feePaid = already_paid >= amount;
  const balancePartiallyPaid = feePaid
    ? 0
    : !depositApplicable
    ? already_paid
    : already_paid > deposit_amount
    ? already_paid - deposit_amount
    : 0;

  const { amount_owing } = currentRegistration?.current_state || {};
  const { attending: wasAttending } = currentRegistration?.item_values || {};

  // Wording to use if activity is in the past and neither being kept open for
  // other purposes nor closed due to an earlier closing deadline having
  // passed. Only used for the main attendance item for the activity; other
  // attendance items (if any) for the same activity are simply left blank
  // (but non-null, so we can still know to disallow registration).

  const pastActivityWording = !is_past ? null : isActivityAttendance ? (
    <Fragment>
      This {isPrimaryActivityAttendance && isProgram ? "program" : "event"} is
      in the past; registration is closed.
    </Fragment>
  ) : (
    <Fragment />
  );

  const tooLateMessage = wasAttending
    ? // For someone attending: keep form open if payment is required.
      // If fully paid, close it after the event is finished (ignore any
      // earlier deadline, which would be for new registrations only).
      amount_owing
      ? null
      : pastActivityWording
    : // For someone not attending: form available until activity is over
      // or until an earlier registration deadline has passed.
      deadline_passed_message ?? pastActivityWording;

  const refundDue = already_paid > amount ? already_paid - amount : 0;
  const refundIsNew =
    refundDue && currentRegistration?.current_state?.total_cost !== amount;
  const refundIsCancellation = wasAttending && !currentDetails?.attending;

  const {
    available,
    availableExcludingRemote,
    waitingList,
  } = capacityInformation(item);

  const onWaitingList = waitingList.position !== null;
  const onWaitingListToAttend = onWaitingList && !wasAttending;
  const onWaitingListForInPerson = onWaitingList && wasAttending;

  const adjustDetails = (changes, clearOtherChanges) => {
    // Once it is too late to submit this registration item, set details to
    // null, which will cause it to be omitted from what is sent to the server.
    if (tooLateMessage) {
      setDetails(null);
      return;
    }

    if (openFor === FORM_STATE.CANCEL && !changes.hasError) {
      setDetails({ attending: false, waitlist_desired: null, changed: true });
      return;
    }

    // Some changes are directly passed to the top level of the object
    // passed to the API endpoint; these we store in directItemChanges.
    // Others are manipulated first, or stored inside a composite object;
    // these we extract and handle separately.

    const directItemChanges = { ...changes };
    const payingDepositOnly = directItemChanges.payingDepositOnly;
    delete directItemChanges.payingDepositOnly;

    const questionChanges = directItemChanges.question_responses;
    delete directItemChanges.question_responses;

    // Create our new details, starting with the current state of our
    // details (or the originals from currentRegistration if we have none
    // yet), then incorporating directItemChanges, then adjusting values
    // that are calculated rather than directly input.

    // If the originals in currentRegistration represent a waiting list
    // posture but space is now available, initialize to the state of
    // claiming the space that is now available. If the originals
    // represent a waiting list posture and space is still not available,
    // initialize waitlist_desired to true to maintain waiting list
    // position. Otherwise, initialize waitlist_desired to null.

    const defaultDetails = { ...(currentRegistration?.item_values || {}) };
    const origWaitlistSetting = onWaitingList ? true : null;
    defaultDetails.waitlist_desired = null;

    if (origWaitlistSetting) {
      const wasWaitingToAttend = !defaultDetails.attending;
      const wasWaitingForInPerson =
        remote_attendance_option && defaultDetails.attending === REMOTELY;

      if (available.toMe > 0 && wasWaitingToAttend) {
        if (remote_attendance_option) delete defaultDetails.attending;
        else defaultDetails.attending = true;
      } else if (availableExcludingRemote.toMe > 0 && wasWaitingForInPerson) {
        defaultDetails.attending = IN_PERSON;
      } else if (available.toMe === 0 && wasWaitingToAttend) {
        defaultDetails.waitlist_desired = true;
      } else if (availableExcludingRemote.toMe === 0 && wasWaitingForInPerson) {
        defaultDetails.waitlist_desired = true;
      }
    }

    const newDetails = {
      ...defaultDetails,
      ...(clearOtherChanges ? {} : currentDetails ?? {}),
      ...directItemChanges,
    };

    // If no space available to attend, don't fill in any other details or
    // mark missing items as incomplete; simply set "changed" based on whether
    // waitlsit status has changed, and return.

    if (available.toMe === 0 && !wasAttending) {
      setDetails({
        waitlist_desired: newDetails.waitlist_desired,
        attending: false,
        changed: newDetails.waitlist_desired !== origWaitlistSetting,
      });
      return;
    }

    // Clone non-scalar objects, so we can make changes without altering the
    // original object

    newDetails.fee_selection_data = {
      ...(newDetails.fee_selection_data || {}),
    };
    newDetails.question_responses = {
      ...(newDetails.question_responses || {}),
    };

    // Now add the changes that are not in directItemChanges.
    if (payingDepositOnly !== undefined) {
      if (payingDepositOnly)
        newDetails.fee_selection_data.paying_deposit = true;
      else delete newDetails.fee_selection_data.paying_deposit;
    } else {
      // If we are initializing items for a registration where deposits are
      // applicable, and we don't have an explicit payingDepositOnly setting
      // to make yet, initialize it to the OPPOSITE of the current state,
      // if any. That is: on initial registration, or any registration
      // where a deposit has not yet been paid, have payingDepositOnly
      // default to true. After a deposit has already paid, have
      // the new payingDepositOnly default to false, for payment of full
      // balance.
      if (clearOtherChanges && depositApplicable)
        newDetails.fee_selection_data.paying_deposit = newDetails
          .fee_selection_data.paying_deposit
          ? false
          : true;
    }

    // Now make any applicable changes to calculated values. This includes
    // values sent to the API endpoint as well as ones that are purely for
    // the front end's use, such as "changed" and "incomplete".

    // Also initialize attending to true if this is the primary activity,
    // we know the person is attending the primary activity (because they
    // are opening a "registration for" or "change registration for" the
    // activity, not a cancellation form, and the activity has space) and
    // there is no need for a remote/in-person attendance prompt. (Note that,
    // if we are cancelling registration, we have already returned above,
    // so this code will not be executed). Note that we must initialize
    // attending to true BEFORE calculating the fee, since the fee will be
    // zero if attending is not set.

    // We initialize attending to false if this is for "program_itself"
    // registration where the person has already indicated they are attending
    // the program but there is no long-term visit attendance record; older
    // registrations did not explicitly create a long-term-visit attendance
    // record in that case and we don't need to create one here.

    adjustFeeQualifyingDetails(newDetails, item);

    newDetails.quantity = null;

    if (
      isPrimaryActivityAttendance &&
      canAssumeAttendingPrimaryActivity &&
      !remote_attendance_option &&
      !newDetails.attending
    )
      newDetails.attending = true;

    if (
      isProgramVisitAttendance &&
      hadActivityAttendanceRecorded &&
      !currentRegistration &&
      newDetails.attending === undefined
    ) {
      newDetails.attending = false;
    }

    const newFee = getApplicableFee(newDetails.attending);
    if (!newFee.depositApplicable)
      delete newDetails.fee_selection_data.paying_deposit;

    newDetails.unit_cost = newDetails.fee_selection_data.paying_deposit
      ? newFee.depositAmountDue
      : newFee.totalAmountDue;

    newDetails.incomplete = null;
    newDetails.changed = false;

    if (
      openFor !== FORM_STATE.CLOSED &&
      (isActivityAttendance || attendingActivity)
    ) {
      const attendance_unspecified =
        (remote_attendance_option &&
          availableExcludingRemote.toMe === 0 &&
          wasAttending !== IN_PERSON &&
          !newDetails.waitlist_desired &&
          newDetails.waitlist_desired !== null) ||
        newDetails.attending === null ||
        newDetails.attending === undefined;

      if (attendance_unspecified)
        newDetails.incomplete =
          (isPrimaryActivityAttendance
            ? "Please specify how you are "
            : "Please specify whether or not you are ") +
          attending_wording.full_sentence_case;

      if (isSimpleTrue(newDetails.attending) && remote_attendance_option)
        newDetails.incomplete =
          "Please specify how you are " + attending_wording.full_sentence_case;

      if (
        !currentRegistration &&
        !(
          isProgramVisitAttendance &&
          hadActivityAttendanceRecorded &&
          !newDetails.attending
        )
      )
        newDetails.changed = true;
      if (
        currentRegistration &&
        newDetails.attending !== currentRegistration.item_values.attending
      ) {
        newDetails.changed = true;
      }
      if (newDetails.unit_cost > 0) newDetails.changed = true;

      if (prompt_attendance_dates) {
        if (!newDetails.attending) newDetails.attendance_dates = null;
        else {
          if (
            !newDetails.attendance_dates ||
            newDetails.attendance_dates.filter(
              (d) => !d.arrival || !d.departure
            ).length > 0
          )
            if (!newDetails.incomplete)
              newDetails.incomplete = "Please specify attendance dates"; // FIXME: wording
        }
      } else newDetails.attendance_dates = null;

      adjustQuestionResponses(newDetails.question_responses, questionChanges);
      ["attendance_dates", "question_responses"].forEach((field) => {
        if (
          currentRegistration &&
          !deepEquals(newDetails[field], currentRegistration.item_values[field])
        )
          newDetails.changed = true;
      });

      if (newDetails.waitlist_desired !== origWaitlistSetting)
        newDetails.changed = true;
    }

    setDetails(newDetails);
  };

  const participationState = useContext(participationStateContext);

  const [haveClearedDetails, setHaveClearedDetails] = useState(false);

  const resetForm =
    (!tooLateMessage && !currentDetails) ||
    openFor !== previouslyOpenFor ||
    participationState.changed.baseData ||
    participationState.changed.itemBaseData[itemKey];

  // If we have been opened in a defferent state, or item or participation
  // data has changed on the server, or we are opened for the first time and
  // have no currentDetails yet, initialize current details and set
  // "haveClearedDetails" (unless haveClearedDetails is already set, in which
  // case do nothing, to prevent an infinite loop. Normally we would use a
  // dependencies array to prevent an infinite loop, but the dependencies of
  // adjustDetails are too complex to include.)  Once have have been
  // re-rendered in a state where there is no need to reset, set
  // haveClearedDetails back to false.
  useEffect(() => {
    if (resetForm && !haveClearedDetails) adjustDetails({}, true);
  });

  useEffect(() => {
    if (resetForm !== !haveClearedDetails) setHaveClearedDetails(resetForm);
  }, [resetForm, haveClearedDetails, setHaveClearedDetails]);

  // If the fee categories have changed, but we aren't doing a full form
  // reset, at least recalculate the total fee owing.
  const [haveRecalculated, setHaveRecalculated] = useState(false);
  const recalculate =
    participationState.changed.itemFeeData[itemKey] && !resetForm;

  useEffect(() => {
    if (recalculate && !haveRecalculated) adjustDetails({}, false);
  });

  useEffect(() => {
    if (recalculate !== haveRecalculated) setHaveRecalculated(recalculate);
  }, [recalculate, haveRecalculated, setHaveRecalculated]);

  const setPayingDepositOnly = (bool) => {
    adjustDetails({ payingDepositOnly: bool });
  };

  const AttendanceErrorMessage = ({ children }) => {
    useEffect(() => {
      if (!currentDetails?.hasError) adjustDetails({ hasError: true });
    }, []);
    return <ErrorMessage>{children}</ErrorMessage>;
  };

  if (fee_categories && applicableCategories.length === 0)
    return (
      <AttendanceErrorMessage>
        Internal error: Unsupported registration fee structure. Please contact
        Institute staff for assistance. Our apologies!
      </AttendanceErrorMessage>
    );
  if (unavailable) return null; // FIXME: deal with this in a better way?

  if (!currentDetails && !tooLateMessage)
    return <div>Loading; please wait ...</div>;
  if (openFor === FORM_STATE.CANCEL)
    return already_paid > 0 && wasAttendingActivity ? (
      <Message color="warning">
        <Message.Body>
          Please note that cancelling your registration does not automatically
          trigger a refund of the {Dollars.format(already_paid)} previously paid
          as a registration fee
          {isPrimaryActivityAttendance
            ? ""
            : isPrimarySubactivityAttendance
            ? " for " + attending_wording.sentence_case
            : " for " + attending_wording.full_sentence_case}
          . <RefundContactNotice ofWhat="part of this amount" isNew />
        </Message.Body>
      </Message>
    ) : null;

  if (openFor === FORM_STATE.CLOSED)
    return currentRegistration?.current_state?.attending &&
      confirmation_display ? (
      <Box>
        <Heading size={4}>
          Information for {attending_wording.full_title_case}
        </Heading>
        <div className="block">
          <StructuredText data={confirmation_display} />
        </div>
      </Box>
    ) : null;
  if (tooLateMessage)
    return (
      <Message {...(isPrimaryActivityAttendance ? { color: "danger" } : {})}>
        <Message.Body>{tooLateMessage}</Message.Body>
      </Message>
    );

  const Emphatic = ({ children }) => (
    <Element italic textWeight="bold" renderAs="span">
      {children}
    </Element>
  );

  if (!isActivityAttendance && !attendingActivity) return null;

  const regularPrompt = (
    <Field>
      <Label>
        {isPrimaryActivityAttendance ? "How " : null}
        Are You {attending_wording.title_case}?
      </Label>
      <PromptAttendance
        attending={currentDetails.attending}
        origAttending={wasAttending}
        setAttending={(v) => adjustDetails({ attending: v })}
        hasRemoteOption={remote_attendance_option}
        radioName={"attending-" + itemKey}
        promptHowOnly={isPrimaryActivityAttendance}
      />
    </Field>
  );

  const remoteOnlyPrompt = (
    <Field>
      <Label>
        {isPrimaryActivityAttendance ? "How " : null}
        Are You {attending_wording.title_case}?
      </Label>
      <PromptAttendance
        attending={
          currentDetails.attending === REMOTELY &&
          currentDetails.waitlist_desired
            ? IN_PERSON
            : currentDetails.attending
        }
        origAttending={onWaitingListForInPerson ? IN_PERSON : wasAttending}
        setAttending={(v) =>
          adjustDetails({
            attending: v === IN_PERSON ? REMOTELY : v,
            waitlist_desired: v === IN_PERSON ? true : null,
          })
        }
        hasRemoteOption={remote_attendance_option}
        radioName={"attending-" + itemKey}
        promptHowOnly={isPrimaryActivityAttendance}
        inPersonWording={
          "remotely, " +
          (onWaitingListForInPerson
            ? "and stay on in-person waiting list"
            : "and go on waiting list for in-person attendance")
        }
        remoteWording={
          "remotely" +
          (onWaitingListForInPerson
            ? ", and cancel in-person waiting list position"
            : "")
        }
      />
    </Field>
  );

  const attendancePrompt =
    remote_attendance_option &&
    availableExcludingRemote.toMe === 0 &&
    wasAttending !== IN_PERSON
      ? remoteOnlyPrompt
      : regularPrompt;

  return (
    <Fragment>
      {isProgramVisitAttendance ? null : isActivityAttendance ? (
        showMainAttendanceHeading ? (
          <Heading size={5} className="registration-item">
            {attending_wording.label_title_case || "Main Registration"}
          </Heading>
        ) : null
      ) : (
        <Heading size={5} className="registration-item">
          {attending_wording.label_title_case}
        </Heading>
      )}
      {last_day_available && !wasAttending ? (
        <Message color="warning">
          <Message.Body>
            The deadline for registering is{" "}
            {dayjs(last_day_available).format("dddd, MMMM D, YYYY")} (Toronto
            time).
          </Message.Body>
        </Message>
      ) : null}
      {onWaitingListToAttend && available.toMe > 0 ? (
        <Message color="success">
          <Message.Header>Space is Now Available</Message.Header>
          <Message.Body>
            You have been on a waiting list, but space is now available, so you
            may now register to attend.
          </Message.Body>
        </Message>
      ) : null}
      {onWaitingListForInPerson &&
      remote_attendance_option &&
      availableExcludingRemote.toMe > 0 ? (
        <Message color="success">
          <Message.Header>
            Space is Now Available for In-Person Attendance
          </Message.Header>
          <Message.Body>
            You have registered to attend remotely and have been on a waiting
            list for in-person attendance. In-person space is now available, so
            you may now register to attend in person.
          </Message.Body>
        </Message>
      ) : null}

      {remote_attendance_option &&
      availableExcludingRemote.toMe === 0 &&
      (available.toMe > 0 || wasAttending) &&
      wasAttending !== IN_PERSON ? (
        <Message color="warning">
          <Message.Header>
            Capacity has been Reached for In-Person Attendance
          </Message.Header>
          <Message.Body>
            There are no more spaces available for{" "}
            {attending_wording.sentence_case} in person.{" "}
            {onWaitingListForInPerson ? (
              <Fragment>
                You are currently registered for remote attendance, and are at
                position #{waitingList.position + 1} on a waiting list for
                in-person attendance. You continue on the waiting list, or
                cancel if you no longer desire in-person attendance.
              </Fragment>
            ) : (
              <Fragment>
                You may register to attend remotely, and may, if desired, place
                your name on a waiting list for in-person attendance in case of
                cancellations. There{" "}
                {waitingList.lengthForInPerson === 0
                  ? "are no people "
                  : waitingList.lengthForInPerson === 1
                  ? "is one person "
                  : `are ${waitingList.lengthForInPerson} people `}
                currently on the waiting list.
              </Fragment>
            )}
          </Message.Body>
        </Message>
      ) : null}
      {available.toMe === 0 && !wasAttending ? (
        <PromptWaitingList
          {...{
            isActivityAttendance,
            isProgram,
            waitingList,
            currentDetails,
            adjustDetails,
          }}
        />
      ) : isPrimaryActivityAttendance && !remote_attendance_option ? null : (
        attendancePrompt
      )}
      {currentDetails.attending && fee_categories ? (
        <Fragment>
          <AskFeeQualifyingData item={item} />
          <Field>
            <Label>Registration Fee</Label>
            <p>
              {fee_categories.length > 1 ? "Your" : "The"} registration fee
              {currentDetails.attending === REMOTELY
                ? " for remote attendance"
                : currentDetails.attending === IN_PERSON
                ? " for in-person attendance"
                : ""}{" "}
              is {Dollars.format(amount)}
              {feeInfo.description !== ""
                ? " (" + feeInfo.description + ")"
                : ""}
              .
            </p>
            {amount === 0 ? null : feePaid ? (
              <p>
                The fee has been fully paid; no further payment is required.
              </p>
            ) : depositApplicable ? (
              <Fragment>
                <p>
                  A deposit of {Dollars.format(deposit_amount)}{" "}
                  {depositPaid
                    ? "has already been paid"
                    : depositPartiallyPaid
                    ? "has been partially paid, with the remainder (" +
                      Dollars.format(deposit_amount - depositPartiallyPaid) +
                      ") due by "
                    : "is due by " + deposit_deadline}
                  ; the balance is due by {deadline}.
                  {balancePartiallyPaid ? (
                    <Fragment>
                      {" "}
                      ({Dollars.format(balancePartiallyPaid)} of the balance has
                      already been paid.)
                    </Fragment>
                  ) : null}
                </p>
                <Control>
                  <Radio
                    name={"paying-full-for-" + itemKey}
                    value="0"
                    checked={
                      !!currentDetails.fee_selection_data?.paying_deposit
                    }
                    onChange={() => setPayingDepositOnly(true)}
                  >
                    {depositPaid
                      ? "Pay full amount later. Payment today: " +
                        Dollars.format(0)
                      : "Pay " +
                        (depositPartiallyPaid ? "remainder of " : "") +
                        "deposit now: " +
                        Dollars.format(deposit_amount)}
                  </Radio>
                </Control>
                <Control>
                  <Radio
                    name={"paying-full-for-" + itemKey}
                    value="1"
                    checked={!currentDetails.fee_selection_data?.paying_deposit}
                    onChange={() => setPayingDepositOnly(false)}
                  >
                    Pay{" "}
                    {depositPaid
                      ? balancePartiallyPaid
                        ? "remainder of balance"
                        : "balance"
                      : "full amount"}{" "}
                    now: {Dollars.format(amount - already_paid)}
                  </Radio>
                </Control>
              </Fragment>
            ) : already_paid > 0 ? (
              <p>
                Of this, {Dollars.format(already_paid)} has already been paid,
                so the amount due is {Dollars.format(totalAmountDue)}.
              </p>
            ) : null}
          </Field>
          {amount > 0 && amount > already_paid && cancellationPolicy ? (
            <Field>
              <StructuredText data={cancellationPolicy} />
            </Field>
          ) : null}
        </Fragment>
      ) : null}
      {refundDue ? (
        <Field>
          {refundIsCancellation ? (
            <Emphatic>
              Please note that cancelling registration does not automatically
              trigger a registration fee refund.
            </Emphatic>
          ) : refundIsNew ? (
            <Fragment>
              Please note: this will result in a lower registration fee than the{" "}
              {Dollars.format(already_paid)} previously paid, but{" "}
              <Emphatic>
                making this change does not automatically trigger a refund.
              </Emphatic>
            </Fragment>
          ) : (
            <Fragment>
              <i>
                You previously paid a registration fee of{" "}
                {Dollars.format(already_paid)}
              </i>
              ,{" "}
              {amount > 0 ? (
                <Fragment>
                  more than the {Dollars.format(amount)} currently required
                </Fragment>
              ) : (
                <Fragment>which is no longer required</Fragment>
              )}
              .
            </Fragment>
          )}{" "}
          <RefundContactNotice
            ofWhat={`part of the ${Dollars.format(refundDue)} ${
              amount > 0 ? "overpayment" : "previous payment"
            }`}
            isNew={refundIsNew}
          />
        </Field>
      ) : null}
      {currentDetails.attending && prompt_attendance_dates ? (
        <PromptAttendanceDates
          isProgram={isProgram}
          currentDates={currentDetails.attendance_dates}
          setDates={(d) => adjustDetails({ attendance_dates: d })}
          startDate={dayjs(participation.activity.start_date)}
          endDate={dayjs(participation.activity.end_date)}
        />
      ) : null}
      {currentDetails.attending ? (
        <PromptQuestions
          questions={questions}
          currentAnswers={currentDetails.question_responses}
          adjustAnswers={(newAnswers) =>
            adjustDetails({ question_responses: newAnswers })
          }
        />
      ) : null}
    </Fragment>
  );
}
